import { categories, parseBECategoryToCategory, type Category } from 'constants/categories';
import type { EventPositionVo } from 'models';
import { centsFromDollarsStringOrZero } from 'models/dollars';
import type { EventVo } from 'models/event';
import type { PortfolioEvent } from 'models/portfolioEvent';

import { sortPortfolioEvents, type SortPortfolioEventsOptions } from './portfolioEventsSort';
import { toPortfolioEventsFromVos } from './portfolioEventTransform';

export const PORTFOLIO_OPEN_PAGE_SIZE = 30;

export interface PortfolioOpenAndPendingRouteResponse {
    /** Open positions, including combo / MVE events inline. */
    open: PortfolioEvent[];
    pending: PortfolioEvent[];
    earlyExits: PortfolioEvent[];
    /** True when more open pages are available. */
    hasMore: boolean;
    totalOpen: number;
    /**
     * Slug list of every category present across the user's open + pending
     * positions, computed before the per-request `category` filter is
     * applied.
     */
    availableCategories: Category[];
    /**
     * Ordered list of open-event tickers after sort + category filter. Used
     * by the client to drive ticker-based next-page fetches.
     */
    openEventTickers: string[];
    /**
     * @deprecated Legacy combos-hub fields, always empty/zero now that combo
     * positions render inline. Kept in the payload for one deploy cycle so
     * that a browser tab still running the pre-removal client bundle (which
     * reads these into the old Combos-tab store slices) doesn't crash on
     * `undefined.length` before it reloads. Safe to delete once no such
     * clients remain active.
     */
    openCombos: PortfolioEvent[];
    hasMoreCombos: boolean;
    totalOpenCombos: number;
    openCombosEventTickers: string[];
}

interface BuildOpenAndPendingPortfolioResponseArgs {
    eventsMap: Map<string, EventVo>;
    openPositions: EventPositionVo[];
    pendingPositions: EventPositionVo[];
    sortOptions?: Partial<SortPortfolioEventsOptions>;
    category?: Category;
    pageNumber?: number;
    pageSize?: number;
}

const isPendingEventPosition = (ep: EventPositionVo): boolean =>
    ep.market_positions.some(
        (mp) => Math.abs(centsFromDollarsStringOrZero(mp.position_cost_dollars)) > 0 && mp.is_closed && !mp.is_settled,
    );

const matchesPortfolioCategory = (
    ep: EventPositionVo,
    eventsMap: Map<string, EventVo>,
    category: Category | undefined,
): boolean => {
    const eventVo = eventsMap.get(ep.event_ticker ?? '');
    if (!eventVo) return false;

    return !category || parseBECategoryToCategory(eventVo.category) === category;
};

const sortEvents = (events: PortfolioEvent[], sortOptions: Partial<SortPortfolioEventsOptions> | undefined) => {
    if (!sortOptions?.criteria) return events;
    return sortPortfolioEvents(events, {
        criteria: sortOptions.criteria,
        reversed: sortOptions.reversed,
        priceChangePeriod: sortOptions.priceChangePeriod,
        positionValueChangePeriod: sortOptions.positionValueChangePeriod,
        totalValueInDollars: sortOptions.totalValueInDollars,
    });
};

const toSortedPortfolioEvents = (
    eventsMap: Map<string, EventVo>,
    eventPositions: EventPositionVo[],
    bucket: 'open' | 'pending' | 'history',
    sortOptions: Partial<SortPortfolioEventsOptions> | undefined,
): PortfolioEvent[] => sortEvents(toPortfolioEventsFromVos(eventsMap, eventPositions, bucket), sortOptions);

export const buildOpenAndPendingPortfolioResponse = ({
    eventsMap,
    openPositions,
    pendingPositions,
    sortOptions,
    category,
    pageNumber,
    pageSize,
}: BuildOpenAndPendingPortfolioResponseArgs): PortfolioOpenAndPendingRouteResponse => {
    const availableCategoriesSet = new Set<Category>();
    for (const ep of openPositions) {
        const eventVo = eventsMap.get(ep.event_ticker ?? '');
        if (eventVo) availableCategoriesSet.add(parseBECategoryToCategory(eventVo.category));
    }
    for (const ep of pendingPositions) {
        if (!isPendingEventPosition(ep)) continue;
        const eventVo = eventsMap.get(ep.event_ticker ?? '');
        if (eventVo) availableCategoriesSet.add(parseBECategoryToCategory(eventVo.category));
    }

    const matches = (ep: EventPositionVo): boolean => matchesPortfolioCategory(ep, eventsMap, category);
    const allOpen = openPositions.filter(matches);
    const allPending = pendingPositions.filter(matches);

    const openEvents = toSortedPortfolioEvents(eventsMap, allOpen, 'open', sortOptions);
    const pendingEvents = toSortedPortfolioEvents(eventsMap, allPending, 'pending', sortOptions);
    const earlyExitEvents = toSortedPortfolioEvents(eventsMap, allPending, 'history', sortOptions);

    const size = Math.max(1, pageSize ?? PORTFOLIO_OPEN_PAGE_SIZE);
    const page = Math.max(1, pageNumber ?? 1);
    const start = (page - 1) * size;
    const pendingSlice = page === 1 ? pendingEvents : [];
    const earlyExitSlice = page === 1 ? earlyExitEvents : [];

    return {
        open: openEvents.slice(start, start + size),
        pending: pendingSlice,
        earlyExits: earlyExitSlice,
        hasMore: openEvents.length > start + size,
        totalOpen: openEvents.length,
        availableCategories: categories.filter((c) => availableCategoriesSet.has(c)),
        openEventTickers: openEvents.map((e) => e.eventTicker),
        openCombos: [],
        hasMoreCombos: false,
        totalOpenCombos: 0,
        openCombosEventTickers: [],
    };
};

export const buildOpenPortfolioEventsByTickerOrder = (
    eventsMap: Map<string, EventVo>,
    eventPositions: EventPositionVo[],
    eventTickers: string[],
): PortfolioEvent[] => {
    const positionsByTicker = new Map(eventPositions.map((ep) => [ep.event_ticker, ep]));
    const orderedPositions = eventTickers.flatMap((ticker) => {
        const ep = positionsByTicker.get(ticker);
        return ep ? [ep] : [];
    });
    return toPortfolioEventsFromVos(eventsMap, orderedPositions, 'open');
};
